import { format } from "date-fns";
import type { Ref } from "vue";
import { reactive, ref, watch } from "vue";
import { findTimezoneByOffset, findTimezoneByZone } from "@/util/timezones";
import type { DateTimeState, OccurrenceModelValue, TimeZone } from "../types";

export const useOccurrenceDateTimes = (occurrence: Ref<OccurrenceModelValue>, defaultTimeZone: Ref<number>) => {
  const startDateTime = reactive<DateTimeState>({ date: "", time: "" });
  const endDateTime = reactive<DateTimeState>({ date: "", time: "" });
  const registrationDateTime = reactive<DateTimeState>({ date: "", time: "" });
  const timeZone = ref<TimeZone | undefined>(undefined);

  const setTimeZone = (timeZoneAttr: string | number, fromAttr?: string, countryCode?: string): void => {
    if (fromAttr === "offset") {
      timeZone.value = findTimezoneByOffset(timeZoneAttr as number, countryCode);
    } else {
      timeZone.value = findTimezoneByZone((timeZoneAttr as string).replace("\\", ""));
    }
  };

  const toFormattedDateTime = (date: string, time: string): string | undefined =>
    date && time ? format(new Date(`${date}T${time}`), "yyyy-MM-dd HH:mm") : undefined;

  const updateDateTimes = (): void => {
    occurrence.value.end_date_time = toFormattedDateTime(endDateTime.date, endDateTime.time);
    occurrence.value.start_date_time = toFormattedDateTime(startDateTime.date, startDateTime.time);
    // Local time in the occurrence's time zone, exactly like start/end —
    // the DB value and the mobile app show it verbatim, so no UTC conversion.
    occurrence.value.registration_deadline = timeZone.value
      ? toFormattedDateTime(registrationDateTime.date, registrationDateTime.time)
      : undefined;
    occurrence.value.time_zone = timeZone.value?.timeZone;
  };

  const setInitialDatesTimes = (): void => {
    if (occurrence.value.start_date_time) {
      const [date, time] = occurrence.value.start_date_time.split("T");
      startDateTime.date = date;
      startDateTime.time = time;
    }
    if (occurrence.value.end_date_time) {
      const [date, time] = occurrence.value.end_date_time.split("T");
      endDateTime.date = date;
      endDateTime.time = time;
    }
    if (occurrence.value.registration_deadline) {
      const [date, time] = occurrence.value.registration_deadline.replace(" ", "T").split("T");
      registrationDateTime.date = date;
      registrationDateTime.time = time?.slice(0, 5) ?? "";
    }
    if (occurrence.value?.time_zone) setTimeZone(occurrence.value.time_zone);
    // No saved zone and no event-location default: fall back to the browser's
    // own timezone rather than leaving the field empty. The resolved zone id
    // can be absent from the option list (some environments resolve to plain
    // "UTC", which newer ICU drops from supportedValuesOf), so fall back once
    // more to a zone matching the browser's current UTC offset.
    if (!timeZone.value) setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (!timeZone.value) setTimeZone(-new Date().getTimezoneOffset(), "offset");
  };

  watch(defaultTimeZone, () => {
    if (!timeZone.value) setTimeZone(defaultTimeZone.value, "offset");
  });

  watch(
    () => occurrence.value.is_asynchronous,
    (isAsync) => {
      if (isAsync) {
        startDateTime.date = "";
        startDateTime.time = "";
        occurrence.value.start_date_time_is_final = false;
        endDateTime.date = "";
        endDateTime.time = "";
        occurrence.value.end_date_time_is_final = false;
      }
    },
  );

  return {
    startDateTime,
    endDateTime,
    registrationDateTime,
    timeZone,
    updateDateTimes,
    setInitialDatesTimes,
    setTimeZone,
  };
};
